import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "wouter";
import { useListLeads, useBulkLeadAction, useListSpaces, getListLeadsQueryKey, getListSpacesQueryKey, getGetLeadsSummaryQueryKey, LeadStage, LeadSource, SpaceType, Urgency, CallStatus, type BulkLeadActionBody, type BulkLeadActionResult } from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, ArrowUp, ArrowDown, ArrowUpDown, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PerformanceSummary, type SummaryFilters } from "@/components/performance-summary";
import { ImportHistory } from "@/components/import-history";
import { maskMobileNumber } from "@/lib/format";

type SortKey = "customerName" | "spaceType" | "stage" | "callStatus" | "urgency" | "priorityScore" | "createdAt";
type SortDir = "asc" | "desc";

/**
 * URL-backed filter state. Reading returns the current query-param value (or `defaultValue`
 * if absent); writing mutates the URL in-place via history.replaceState so the entry isn't
 * flooded when users type / click. Whole-page re-renders happen naturally on re-render.
 */
function useQueryString<T extends string>(
  key: string,
  defaultValue: T,
): [T, (v: T) => void] {
  const [params, setParams] = useSearchParams();
  const current = (params.get(key) as T | null) ?? defaultValue;
  const setter = useCallback(
    (v: T) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!v || v === defaultValue) next.delete(key);
          else next.set(key, v);
          return next;
        },
        { replace: true },
      );
    },
    [key, defaultValue, setParams],
  );
  return [current, setter];
}

function useQueryNumber(key: string, defaultValue: number): [number, (v: number) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const current = raw && !Number.isNaN(Number(raw)) ? Number(raw) : defaultValue;
  const setter = useCallback(
    (v: number) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v === defaultValue) next.delete(key);
          else next.set(key, String(v));
          return next;
        },
        { replace: true },
      );
    },
    [key, defaultValue, setParams],
  );
  return [current, setter];
}

export default function Leads() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const { data: spaces } = useListSpaces({ query: { queryKey: getListSpacesQueryKey(), enabled: isAdmin } });
  const [, setParams] = useSearchParams();
  const [search, setSearch] = useQueryString<string>("search", "");
  const [searchDraft, setSearchDraft] = useState(search);
  // Sync the draft when URL search changes (e.g. via back/forward or clear button).
  useEffect(() => { setSearchDraft(search); }, [search]);
  const applySearch = () => setSearch(searchDraft.trim());
  const clearSearch = () => { setSearchDraft(""); setSearch(""); };
  const [sortKey, setSortKey] = useQueryString<SortKey>("sortKey", "priorityScore");
  const [sortDir, setSortDir] = useQueryString<SortDir>("sortDir", "desc");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkAction, setBulkAction] = useState<BulkLeadActionBody["action"]>("set_stage");
  const [bulkStage, setBulkStage] = useState<LeadStage>(LeadStage.contacted);
  const [bulkLeadStatus, setBulkLeadStatus] = useState<string>("qualified");
  const [bulkTemplate, setBulkTemplate] = useState<string>("default_intro");
  const [bulkTargetSpaceId, setBulkTargetSpaceId] = useState<string>("");
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState<{
    action: BulkLeadActionBody["action"];
    targetSpaceName: string | null;
    attempted: number;
    result: BulkLeadActionResult;
  } | null>(null);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-50" />;
    return sortDir === "asc"
      ? <ArrowUp className="ml-1 inline h-3 w-3" />
      : <ArrowDown className="ml-1 inline h-3 w-3" />;
  };
  const [stageFilter, setStageFilter] = useQueryString<LeadStage | "ALL">("stage", "ALL");
  const [urgencyFilter, setUrgencyFilter] = useQueryString<Urgency | "ALL">("urgency", "ALL");
  const [sourceFilter, setSourceFilter] = useQueryString<LeadSource | "ALL">("source", "ALL");
  const [spaceTypeFilter, setSpaceTypeFilter] = useQueryString<SpaceType | "ALL">("spaceType", "ALL");
  const [callStatusFilter, setCallStatusFilter] = useQueryString<CallStatus | "ALL">("callStatus", "ALL");
  const [leadStatusFilter, setLeadStatusFilter] = useQueryString<string>("leadStatus", "ALL");
  const [page, setPage] = useQueryNumber("page", 1);
  const [pageSize, setPageSize] = useQueryNumber("pageSize", 25);

  const formatDateInput = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  type DatePreset = "today" | "yesterday" | "7d" | "30d" | "custom";
  const todayStr = formatDateInput(new Date());
  const [from, setFrom] = useQueryString<string>("from", todayStr);
  const [to, setTo] = useQueryString<string>("to", todayStr);

  // Derive which preset (if any) matches the current from/to range.
  const datePreset: DatePreset = useMemo(() => {
    const t = new Date();
    const fmt = formatDateInput;
    if (from === fmt(t) && to === fmt(t)) return "today";
    const y = new Date(); y.setDate(t.getDate() - 1);
    if (from === fmt(y) && to === fmt(y)) return "yesterday";
    const w = new Date(); w.setDate(t.getDate() - 6);
    if (from === fmt(w) && to === fmt(t)) return "7d";
    const m = new Date(); m.setDate(t.getDate() - 29);
    if (from === fmt(m) && to === fmt(t)) return "30d";
    return "custom";
  }, [from, to]);

  const applyDatePreset = (p: DatePreset) => {
    if (p === "custom") return;
    const t = new Date();
    const f = new Date();
    if (p === "yesterday") { f.setDate(t.getDate() - 1); t.setDate(t.getDate() - 1); }
    else if (p === "7d") { f.setDate(t.getDate() - 6); }
    else if (p === "30d") { f.setDate(t.getDate() - 29); }
    const fStr = formatDateInput(f);
    const tStr = formatDateInput(t);
    // Update both `from` and `to` in one URL mutation to avoid a two-write race.
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (fStr === todayStr) next.delete("from"); else next.set("from", fStr);
        if (tStr === todayStr) next.delete("to"); else next.set("to", tStr);
        return next;
      },
      { replace: true },
    );
  };
  const onDateChange = (which: "from" | "to", v: string) => {
    if (which === "from") setFrom(v); else setTo(v);
  };

  const fromIso = useMemo(() => {
    const d = new Date(`${from}T00:00:00`);
    return Number.isNaN(d.getTime()) ? new Date(`${todayStr}T00:00:00`).toISOString() : d.toISOString();
  }, [from, todayStr]);
  const toIso = useMemo(() => {
    const d = new Date(`${to}T23:59:59`);
    return Number.isNaN(d.getTime()) ? new Date(`${todayStr}T23:59:59`).toISOString() : d.toISOString();
  }, [to, todayStr]);

  const summaryFilters: SummaryFilters = useMemo(() => ({
    search: search || undefined,
    stage: stageFilter !== "ALL" ? stageFilter : undefined,
    urgency: urgencyFilter !== "ALL" ? urgencyFilter : undefined,
    source: sourceFilter !== "ALL" ? sourceFilter : undefined,
    spaceType: spaceTypeFilter !== "ALL" ? spaceTypeFilter : undefined,
    callStatus: callStatusFilter !== "ALL" ? callStatusFilter : undefined,
    leadStatus: leadStatusFilter !== "ALL" ? leadStatusFilter : undefined,
  }), [search, stageFilter, urgencyFilter, sourceFilter, spaceTypeFilter, callStatusFilter, leadStatusFilter]);

  const filtersActive = Object.values(summaryFilters).some((v) => v != null && v !== "");

  // Reset to page 1 whenever any filter or page size changes
  useEffect(() => { setPage(1); }, [search, stageFilter, urgencyFilter, sourceFilter, spaceTypeFilter, callStatusFilter, leadStatusFilter, pageSize, from, to]);

  const { data: leadsResponse, isLoading } = useListLeads({
    ...summaryFilters,
    from: fromIso,
    to: toIso,
    page,
    pageSize,
  });
  const leads = leadsResponse?.items;
  const totalLeads = leadsResponse?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalLeads / pageSize));

  const bulkMutation = useBulkLeadAction({
    mutation: {
      onSuccess: (result, variables) => {
        const body = variables.data;
        const targetSpaceName = body.action === "reassign_space"
          ? (spaces ?? []).find((s) => s.id === body.targetSpaceId)?.name ?? null
          : null;
        setBulkResult({
          action: body.action,
          targetSpaceName,
          attempted: body.leadIds.length,
          result,
        });
        setSelected(new Set());
        void queryClient.invalidateQueries({ queryKey: getListLeadsQueryKey() });
        void queryClient.invalidateQueries({ queryKey: getGetLeadsSummaryQueryKey() });
      },
      onError: () => toast({ title: "Bulk action failed", variant: "destructive" }),
    },
  });

  const urgencyRank: Record<string, number> = { low: 0, medium: 1, high: 2, immediate: 3 };
  const stageRank: Record<string, number> = {
    not_contacted: 0, contacted: 1, proposal_sent: 2, tour_scheduled: 3,
    tour_done: 4, follow_up: 5, closed_won: 6, closed_loss: 7,
  };
  // Sort along the call-progress funnel: from "haven't tried" → ramped attempts → resolution.
  const callStatusRank: Record<string, number> = {
    not_started: 0, not_reachable: 1, not_answered: 2,
    not_answered_1: 3, not_answered_2: 4, not_answered_3: 5,
    follow_up_1: 6, follow_up_2: 7, follow_up_3: 8, follow_up: 9,
    connected: 10, disconnected: 11,
  };
  const sortedLeads = useMemo(() => leads ? [...leads].sort((a, b) => {
    let av: string | number;
    let bv: string | number;
    if (sortKey === "urgency") { av = urgencyRank[a.urgency] ?? 0; bv = urgencyRank[b.urgency] ?? 0; }
    else if (sortKey === "stage") { av = stageRank[a.stage] ?? 0; bv = stageRank[b.stage] ?? 0; }
    else if (sortKey === "callStatus") { av = callStatusRank[a.callStatus] ?? 0; bv = callStatusRank[b.callStatus] ?? 0; }
    else if (sortKey === "priorityScore") { av = a.priorityScore; bv = b.priorityScore; }
    else if (sortKey === "createdAt") { av = new Date(a.createdAt).getTime(); bv = new Date(b.createdAt).getTime(); }
    else { av = String(a[sortKey] ?? "").toLowerCase(); bv = String(b[sortKey] ?? "").toLowerCase(); }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  }) : undefined, [leads, sortKey, sortDir]);

  const allVisibleIds = sortedLeads?.map((l) => l.id) ?? [];
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selected.has(id));

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allVisibleIds));
  };
  const toggleOne = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const requestBulkConfirm = () => setConfirmBulkOpen(true);
  const applyBulk = () => {
    const body: BulkLeadActionBody = {
      leadIds: Array.from(selected),
      action: bulkAction,
      ...(bulkAction === "set_stage" ? { stage: bulkStage } : {}),
      ...(bulkAction === "set_lead_status" ? { leadStatus: bulkLeadStatus } : {}),
      ...(bulkAction === "send_whatsapp_template" ? { templateName: bulkTemplate } : {}),
      ...(bulkAction === "reassign_space" ? { targetSpaceId: Number(bulkTargetSpaceId) } : {}),
    };
    setConfirmBulkOpen(false);
    bulkMutation.mutate({ data: body });
  };

  const getStageColor = (stage: string) => ({
    not_contacted: "bg-gray-100 text-gray-800", contacted: "bg-blue-100 text-blue-800",
    proposal_sent: "bg-purple-100 text-purple-800", tour_scheduled: "bg-amber-100 text-amber-800",
    tour_done: "bg-cyan-100 text-cyan-800", follow_up: "bg-orange-100 text-orange-800",
    closed_won: "bg-green-100 text-green-800", closed_loss: "bg-red-100 text-red-800",
  }[stage] ?? "bg-gray-100 text-gray-800");

  const getPriorityColor = (priority: string) => ({
    urgent: "bg-red-100 text-red-800", high: "bg-orange-100 text-orange-800",
    medium: "bg-yellow-100 text-yellow-800", low: "bg-gray-100 text-gray-700",
  }[priority] ?? "bg-gray-100 text-gray-700");

  const getUrgencyColor = (urgency: string) => ({
    low: "bg-gray-100 text-gray-600", medium: "bg-yellow-100 text-yellow-700",
    high: "bg-orange-100 text-orange-700", immediate: "bg-red-100 text-red-700",
  }[urgency] ?? "bg-gray-100 text-gray-600");

  // Call-status palette per spec:
  //   connected → green, disconnected → red, not_reachable → rose,
  //   not_answered_1/2/3 → amber, deepening, not_started → white.
  // Follow-up_* and not_answered (legacy) get sensible neighbours.
  const getCallStatusColor = (status: string) => ({
    not_started: "bg-white text-gray-700 border border-gray-300",
    not_reachable: "bg-rose-100 text-rose-800",
    not_answered: "bg-amber-50 text-amber-800",
    not_answered_1: "bg-amber-100 text-amber-900",
    not_answered_2: "bg-amber-300 text-amber-950",
    not_answered_3: "bg-amber-500 text-amber-50",
    follow_up: "bg-blue-50 text-blue-800",
    follow_up_1: "bg-blue-100 text-blue-900",
    follow_up_2: "bg-blue-200 text-blue-900",
    follow_up_3: "bg-blue-300 text-blue-950",
    connected: "bg-green-100 text-green-800",
    disconnected: "bg-red-100 text-red-800",
  }[status] ?? "bg-gray-100 text-gray-700");

  // Row-level tint mirrors the badge palette but at a subtler intensity so the
  // table stays readable. Hover state is darkened a notch in the same hue.
  const getCallStatusRowTint = (status: string) => ({
    not_started: "hover:bg-muted/50",
    not_reachable: "bg-rose-50/70 hover:bg-rose-100/80",
    not_answered: "bg-amber-50/50 hover:bg-amber-100/60",
    not_answered_1: "bg-amber-50 hover:bg-amber-100",
    not_answered_2: "bg-amber-100 hover:bg-amber-200",
    not_answered_3: "bg-amber-200 hover:bg-amber-300",
    follow_up: "bg-blue-50/60 hover:bg-blue-100/70",
    follow_up_1: "bg-blue-50 hover:bg-blue-100",
    follow_up_2: "bg-blue-100 hover:bg-blue-200",
    follow_up_3: "bg-blue-200 hover:bg-blue-300",
    connected: "bg-green-50 hover:bg-green-100",
    disconnected: "bg-red-50 hover:bg-red-100",
  }[status] ?? "hover:bg-muted/50");

  return (
    <div className="space-y-6 pb-24">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-3xl font-bold tracking-tight">Leads</h1>
        <Link href="/leads/new" className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2">
          <Plus className="mr-2 h-4 w-4" />
          New Lead
        </Link>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row gap-4 flex-wrap">
          <div className="flex flex-1 min-w-[200px] gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search name, phone, location..."
                className="pl-8 pr-8"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applySearch(); } }}
              />
              {searchDraft && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button type="button" onClick={applySearch} disabled={searchDraft === search}>Search</Button>
          </div>
          <Select value={stageFilter} onValueChange={(v: LeadStage | "ALL") => setStageFilter(v)}>
            <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder="Stage" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Stages</SelectItem>
              {Object.values(LeadStage).map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={urgencyFilter} onValueChange={(v: Urgency | "ALL") => setUrgencyFilter(v)}>
            <SelectTrigger className="w-full sm:w-[160px]"><SelectValue placeholder="Urgency" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Urgencies</SelectItem>
              {Object.values(Urgency).map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={(v: LeadSource | "ALL") => setSourceFilter(v)}>
            <SelectTrigger className="w-full sm:w-[160px]"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Sources</SelectItem>
              {Object.values(LeadSource).map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={spaceTypeFilter} onValueChange={(v: SpaceType | "ALL") => setSpaceTypeFilter(v)}>
            <SelectTrigger className="w-full sm:w-[160px]"><SelectValue placeholder="Space Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Types</SelectItem>
              {Object.values(SpaceType).map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={callStatusFilter} onValueChange={(v: CallStatus | "ALL") => setCallStatusFilter(v)}>
            <SelectTrigger className="w-full sm:w-[160px]"><SelectValue placeholder="Call Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Calls</SelectItem>
              {Object.values(CallStatus).map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={leadStatusFilter} onValueChange={setLeadStatusFilter}>
            <SelectTrigger className="w-full sm:w-[160px]"><SelectValue placeholder="Lead Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Lead Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="disqualified">Disqualified</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
            </SelectContent>
          </Select>
          <div className="w-full border-t my-1" />
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 w-full flex-wrap">
            <div className="flex flex-col gap-1 w-full sm:w-auto">
              <Label htmlFor="lead-from" className="text-xs text-muted-foreground">From</Label>
              <Input id="lead-from" type="date" className="w-full sm:w-[160px]" value={from} max={to} onChange={(e) => onDateChange("from", e.target.value)} />
            </div>
            <div className="flex flex-col gap-1 w-full sm:w-auto">
              <Label htmlFor="lead-to" className="text-xs text-muted-foreground">To</Label>
              <Input id="lead-to" type="date" className="w-full sm:w-[160px]" value={to} min={from} onChange={(e) => onDateChange("to", e.target.value)} />
            </div>
            <div className="flex flex-wrap gap-2">
              {([
                ["today", "Today"], ["yesterday", "Yesterday"],
                ["7d", "Last 7 days"], ["30d", "Last 30 days"], ["custom", "Custom"],
              ] as const).map(([key, label]) => (
                <Button
                  key={key}
                  type="button"
                  size="sm"
                  variant={datePreset === key ? "default" : "outline"}
                  onClick={() => applyDatePreset(key)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <PerformanceSummary
        fromIso={fromIso}
        toIso={toIso}
        fromLabel={from}
        toLabel={to}
        filters={summaryFilters}
        filtersActive={filtersActive}
      />

      <ImportHistory />

      <Card>
        {/* Mobile card list (< md) */}
        <div className="block md:hidden divide-y">
          {isLoading ? (
            [...Array(5)].map((_, i) => (
              <div key={i} className="p-4 space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-40" />
              </div>
            ))
          ) : sortedLeads?.length === 0 ? (
            <div className="h-24 flex items-center justify-center text-sm text-muted-foreground">No leads found.</div>
          ) : (
            sortedLeads?.map((lead) => (
              <div
                key={lead.id}
                className={`flex items-start gap-3 p-4 active:bg-muted/60 ${selected.has(lead.id) ? "bg-muted/40" : ""}`}
                onClick={() => window.location.href = `/leads/${lead.id}`}
              >
                <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                  <Checkbox checked={selected.has(lead.id)} onCheckedChange={() => toggleOne(lead.id)} aria-label={`Select ${lead.customerName}`} />
                </div>
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium truncate">{lead.customerName}</div>
                    <Badge variant="outline" className={`capitalize border-transparent shrink-0 ${getPriorityColor(lead.priority)}`}>{lead.priority}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">{lead.mobileNumber}</div>
                  <div className="text-sm capitalize">
                    {lead.spaceType.replace(/_/g, " ")}
                    {lead.space?.name ? <span className="text-muted-foreground"> · {lead.space.name}</span> : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className={`capitalize border-transparent ${getStageColor(lead.stage)}`}>{lead.stage.replace(/_/g, " ")}</Badge>
                    <Badge variant="outline" className={`capitalize border-transparent ${getUrgencyColor(lead.urgency)}`}>{lead.urgency}</Badge>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {new Date(lead.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop table (md+) */}
        <div className="hidden md:block overflow-x-auto">
        <Table className="min-w-[900px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
              </TableHead>
              <TableHead onClick={() => toggleSort("customerName")} className="cursor-pointer select-none">Customer<SortIcon k="customerName" /></TableHead>
              <TableHead>Contact</TableHead>
              <TableHead onClick={() => toggleSort("spaceType")} className="cursor-pointer select-none">Space Type<SortIcon k="spaceType" /></TableHead>
              <TableHead>Location</TableHead>
              <TableHead onClick={() => toggleSort("stage")} className="cursor-pointer select-none">Stage<SortIcon k="stage" /></TableHead>
              <TableHead onClick={() => toggleSort("callStatus")} className="cursor-pointer select-none">Call Status<SortIcon k="callStatus" /></TableHead>
              <TableHead onClick={() => toggleSort("urgency")} className="cursor-pointer select-none">Urgency<SortIcon k="urgency" /></TableHead>
              <TableHead onClick={() => toggleSort("priorityScore")} className="cursor-pointer select-none">Priority<SortIcon k="priorityScore" /></TableHead>
              <TableHead onClick={() => toggleSort("createdAt")} className="cursor-pointer select-none">Created<SortIcon k="createdAt" /></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-5 w-5" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                </TableRow>
              ))
            ) : sortedLeads?.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="h-24 text-center text-muted-foreground">No leads found.</TableCell></TableRow>
            ) : (
              sortedLeads?.map((lead) => (
                <TableRow key={lead.id} className={`transition-colors ${getCallStatusRowTint(lead.callStatus)} ${selected.has(lead.id) ? "ring-1 ring-inset ring-primary/40" : ""}`}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.has(lead.id)} onCheckedChange={() => toggleOne(lead.id)} aria-label={`Select ${lead.customerName}`} />
                  </TableCell>
                  <TableCell className="font-medium cursor-pointer" onClick={() => window.location.href = `/leads/${lead.id}`}>
                    <Link href={`/leads/${lead.id}`} className="hover:underline">{lead.customerName}</Link>
                  </TableCell>
                  <TableCell className="cursor-pointer" onClick={() => window.location.href = `/leads/${lead.id}`}>
                    <div className="flex flex-col gap-1">
                      <span className="font-mono text-sm">{maskMobileNumber(lead.mobileNumber)}</span>
                      <Badge variant="outline" className={`capitalize w-fit text-[10px] py-0 px-1.5 ${getCallStatusColor(lead.callStatus)} ${lead.callStatus === "not_started" ? "" : "border-transparent"}`}>{lead.callStatus.replace(/_/g, " ")}</Badge>
                    </div>
                  </TableCell>
                  <TableCell className="capitalize cursor-pointer" onClick={() => window.location.href = `/leads/${lead.id}`}>{lead.spaceType.replace(/_/g, " ")}</TableCell>
                  <TableCell className="cursor-pointer" onClick={() => window.location.href = `/leads/${lead.id}`}>{lead.space.name}</TableCell>
                  <TableCell className="cursor-pointer" onClick={() => window.location.href = `/leads/${lead.id}`}>
                    <Badge variant="outline" className={`capitalize ${getStageColor(lead.stage)} border-transparent`}>{lead.stage.replace(/_/g, " ")}</Badge>
                  </TableCell>
                  <TableCell className="cursor-pointer" onClick={() => window.location.href = `/leads/${lead.id}`}>
                    <Badge variant="outline" className={`capitalize ${getCallStatusColor(lead.callStatus)} ${lead.callStatus === "not_started" ? "" : "border-transparent"}`}>{lead.callStatus.replace(/_/g, " ")}</Badge>
                  </TableCell>
                  <TableCell className="cursor-pointer" onClick={() => window.location.href = `/leads/${lead.id}`}>
                    <Badge variant="outline" className={`capitalize ${getUrgencyColor(lead.urgency)} border-transparent`}>{lead.urgency}</Badge>
                  </TableCell>
                  <TableCell className="cursor-pointer" onClick={() => window.location.href = `/leads/${lead.id}`}>
                    <Badge variant="outline" className={`capitalize border-transparent ${getPriorityColor(lead.priority)}`}>{lead.priority} ({lead.priorityScore})</Badge>
                  </TableCell>
                  <TableCell
                    className="cursor-pointer whitespace-nowrap text-sm text-muted-foreground"
                    onClick={() => window.location.href = `/leads/${lead.id}`}
                    title={new Date(lead.createdAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                  >
                    {new Date(lead.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t">
          <div className="text-sm text-muted-foreground">
            {isLoading
              ? "Loading…"
              : totalLeads === 0
                ? "0 leads"
                : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalLeads)} of ${totalLeads}`}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="h-8 w-[80px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 100].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1 || isLoading}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm tabular-nums min-w-[80px] text-center">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages || isLoading}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 md:left-64 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-lg">
          <div className="flex flex-wrap items-center gap-3 p-4">
            <span className="text-sm font-medium">{selected.size} selected</span>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}><X className="mr-1 h-4 w-4" />Clear</Button>
            <div className="h-6 w-px bg-border" />
            <Select value={bulkAction} onValueChange={(v) => setBulkAction(v as BulkLeadActionBody["action"])}>
              <SelectTrigger className="w-[200px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="set_stage">Change Stage</SelectItem>
                <SelectItem value="set_lead_status">Change Lead Status</SelectItem>
                <SelectItem value="send_brochure">Send Brochure</SelectItem>
                <SelectItem value="send_whatsapp_template">WhatsApp Template</SelectItem>
                {isAdmin && <SelectItem value="reassign_space">Reassign to Space</SelectItem>}
              </SelectContent>
            </Select>
            {bulkAction === "reassign_space" && isAdmin && (
              <Select value={bulkTargetSpaceId} onValueChange={setBulkTargetSpaceId}>
                <SelectTrigger className="w-[220px] h-9" data-testid="select-bulk-target-space"><SelectValue placeholder="Select target space" /></SelectTrigger>
                <SelectContent>
                  {(spaces ?? []).map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {bulkAction === "set_stage" && (
              <Select value={bulkStage} onValueChange={(v) => setBulkStage(v as LeadStage)}>
                <SelectTrigger className="w-[180px] h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.values(LeadStage).map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {bulkAction === "set_lead_status" && (
              <Select value={bulkLeadStatus} onValueChange={setBulkLeadStatus}>
                <SelectTrigger className="w-[180px] h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="disqualified">Disqualified</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                </SelectContent>
              </Select>
            )}
            {bulkAction === "send_whatsapp_template" && (
              <Input value={bulkTemplate} onChange={(e) => setBulkTemplate(e.target.value)} placeholder="Template name" className="w-[200px] h-9" />
            )}
            <Button
              onClick={requestBulkConfirm}
              disabled={bulkMutation.isPending || (bulkAction === "reassign_space" && !bulkTargetSpaceId)}
            >
              {bulkMutation.isPending ? "Applying..." : "Apply"}
            </Button>
          </div>
        </div>
      )}
      <Dialog open={confirmBulkOpen} onOpenChange={setConfirmBulkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm bulk action</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>
              Apply <span className="font-semibold">{bulkAction.replace(/_/g, " ")}</span> to <span className="font-semibold">{selected.size} lead{selected.size === 1 ? "" : "s"}</span>?
            </p>
            {bulkAction === "set_stage" && <p className="text-muted-foreground">New stage: <span className="font-mono">{bulkStage}</span></p>}
            {bulkAction === "set_lead_status" && <p className="text-muted-foreground">New status: <span className="font-mono">{bulkLeadStatus}</span></p>}
            {bulkAction === "send_whatsapp_template" && <p className="text-muted-foreground">Template: <span className="font-mono">{bulkTemplate}</span> will be sent to each lead's WhatsApp number.</p>}
            {bulkAction === "send_brochure" && <p className="text-muted-foreground">Brochure PDF will be emailed to each lead's email address. Leads without an email will be skipped and recorded in the activity log.</p>}
            {bulkAction === "reassign_space" && (
              <p className="text-muted-foreground">
                Move to space: <span className="font-mono">{(spaces ?? []).find((s) => String(s.id) === bulkTargetSpaceId)?.name ?? "—"}</span>. Each lead's previous space will be recorded in its activity log.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBulkOpen(false)}>Cancel</Button>
            <Button onClick={applyBulk} disabled={bulkMutation.isPending}>{bulkMutation.isPending ? "Applying..." : "Confirm"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BulkResultDialog result={bulkResult} onClose={() => setBulkResult(null)} />
    </div>
  );
}

const REASON_LABELS: Record<string, string> = {
  not_found: "Lead not found",
  no_access: "No access to lead",
  already_in_target_space: "Already in target space",
  missing_target_space: "Target space missing",
  missing_stage: "Stage not provided",
  missing_lead_status: "Status not provided",
  missing_email: "No email on file",
};

function humanizeReason(reason: string): string {
  if (REASON_LABELS[reason]) return REASON_LABELS[reason];
  if (reason.startsWith("brochure_send_failed:")) return `Brochure send failed (${reason.slice("brochure_send_failed:".length)})`;
  if (reason.startsWith("whatsapp_send_failed:")) return `WhatsApp send failed (${reason.slice("whatsapp_send_failed:".length)})`;
  if (reason.startsWith("exception:")) return `Server error (${reason.slice("exception:".length)})`;
  return reason;
}

function BulkResultDialog({
  result,
  onClose,
}: {
  result: {
    action: BulkLeadActionBody["action"];
    targetSpaceName: string | null;
    attempted: number;
    result: BulkLeadActionResult;
  } | null;
  onClose: () => void;
}) {
  const open = result !== null;
  const skipped = result?.result.skipped ?? [];
  const skippedIds = result?.result.skippedIds ?? [];
  // Build display rows. Prefer the structured `skipped` (with reasons); fall
  // back to skippedIds for older API responses.
  const rows = skipped.length > 0
    ? skipped
    : skippedIds.map((leadId) => ({ leadId, reason: "skipped" }));

  // Group by reason for the count breakdown.
  const groups = new Map<string, number[]>();
  for (const r of rows) {
    const arr = groups.get(r.reason) ?? [];
    arr.push(r.leadId);
    groups.set(r.reason, arr);
  }
  const notFoundCount = groups.get("not_found")?.length ?? 0;
  const succeeded = result?.result.succeeded ?? 0;
  const failed = result?.result.failed ?? 0;
  const movedLabel = result?.action === "reassign_space" ? "Moved" : "Updated";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {result?.action === "reassign_space" ? "Bulk reassign results" : "Bulk action results"}
          </DialogTitle>
          <DialogDescription>
            Breakdown of which leads were updated and which were skipped, with reasons.
          </DialogDescription>
        </DialogHeader>
        {result && (
          <div className="space-y-4 text-sm" data-testid="bulk-result-dialog">
            {result.action === "reassign_space" && result.targetSpaceName && (
              <p className="text-muted-foreground">
                Target space: <span className="font-mono">{result.targetSpaceName}</span>
              </p>
            )}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded border p-3">
                <div className="text-2xl font-semibold text-green-700" data-testid="bulk-result-moved">{succeeded}</div>
                <div className="text-xs text-muted-foreground">{movedLabel}</div>
              </div>
              <div className="rounded border p-3">
                <div className="text-2xl font-semibold text-amber-700" data-testid="bulk-result-skipped">{Math.max(0, failed - notFoundCount)}</div>
                <div className="text-xs text-muted-foreground">Skipped</div>
              </div>
              <div className="rounded border p-3">
                <div className="text-2xl font-semibold text-red-700" data-testid="bulk-result-not-found">{notFoundCount}</div>
                <div className="text-xs text-muted-foreground">Not found</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Attempted on {result.attempted} lead{result.attempted === 1 ? "" : "s"}.
            </p>
            {rows.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium">Skipped leads</div>
                <div className="max-h-64 space-y-3 overflow-y-auto rounded border p-3">
                  {Array.from(groups.entries()).map(([reason, ids]) => (
                    <div key={reason} className="space-y-1">
                      <div className="text-xs font-semibold text-muted-foreground">
                        {humanizeReason(reason)} ({ids.length})
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {ids.map((id) => (
                          reason === "not_found" ? (
                            <span key={id} className="font-mono text-xs text-muted-foreground" data-testid={`bulk-result-lead-${id}`}>
                              #{id}
                            </span>
                          ) : (
                            <Link
                              key={id}
                              href={`/leads/${id}`}
                              className="font-mono text-xs text-blue-600 hover:underline"
                              data-testid={`bulk-result-lead-${id}`}
                            >
                              #{id}
                            </Link>
                          )
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose} data-testid="button-bulk-result-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
